import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Table, TableRow, TableBody, TableCell,
  Typography, TableContainer,
  TablePagination, TableHead, Stack,
  InputAdornment,
  TextField,
  IconButton,
  Card,
  Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import SearchNotFound from '../components/SearchNotFound';
import axios from 'axios';
import { API_URL } from '../constant';
import { includes, isEmpty } from 'lodash';
import LoadingSpinner from './LoadingSpinner';
import moment from 'moment';

export default function DealerLoanList() {
  const { state } = useParams();
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [value, setValue] = useState([]);
  const [filteredValue, setFilteredValue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDealer, setShowDealer] = useState(false);
  const [downloadingLoanId, setDownloadingLoanId] = useState(null);

  useEffect(() => {
    fetchLoanData(state);
  }, [state]);

  const fetchLoanData = async (currentState) => {
      setLoading(true)
    try {
      const response = await axios.get(`${API_URL}/dealer_loans/${currentState}`);
      if (response.data.status === 'success') {
        const dealerLoans = response.data.data?.reverse() || [];
        const showDealer = !isEmpty(dealerLoans?.[0]?.firm_name)
        setShowDealer(showDealer)
        setValue(dealerLoans);
        setFilteredValue(dealerLoans);
      }
    } catch (error) {
      console.error('Error fetching loan data:', error);
    } finally {
      setLoading(false)
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - value.length) : 0;
  const isUserNotFound = !filteredValue.length && !isEmpty(value);

  const redirect = (loanDetails) => {
    const loanId = loanDetails?.loan_id;
    const dealerId =  loanDetails?.dealer_id
    navigate(`/dealers/${dealerId}/loan/${loanId}/details`, { state: { loanDetails } });
  };

  const handleFilter = (e) => {
    const query = e.target.value.toLowerCase();
    const filteredData = value.filter((v) =>
      Object.values(v).some((val) =>
        String(val).toLowerCase().includes(query)
      )
    );
    setFilteredValue(filteredData);
  };

 function formatStateHeader(state) {
  const cleaned = state.replace(/_/g, ' ').replace(/loans?/i, '').trim(); // removes both 'loan' and 'loans'
  const titleCase = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  return `${titleCase} Loans`;
}


 const handleDownloadDo = async (loan) => {
  const loanId = loan?.loan_id;
  const docId = loan?.doc_id;

  if (!loanId || !docId) {
    alert('Delivery Order not available for download.');
    return;
  }

  try {
    setDownloadingLoanId(loanId);
    const response = await axios.get(`${API_URL}/loan/${loanId}/doc/${docId}`);
    const tempUrl = response?.data?.data?.tempFilePaths?.[0]?.tempUrl;

    if (tempUrl) {
      window.open(tempUrl, '_blank');
    } else {
      console.error('Download URL not found in response:', response?.data);
      alert('Download link not available. Please try again later.');
    }
  } catch (error) {
    console.error('Error downloading Delivery Order:', error);
    alert('Unable to download Delivery Order. Please try again.');
  } finally {
    setDownloadingLoanId(null);
  }
 };


  return (
    <Page>
      {loading ? <LoadingSpinner /> :
        <Card sx={{ width: 'auto', p: 3, boxShadow: 3, borderRadius: 2 }}>
          <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={3}
              sx={{ borderBottom: 1, borderColor: 'divider', pb: 2 }}
            >
              <Typography variant="h4" sx={{ fontFamily:'sans-serif',  borderBottom: 1, borderColor: 'secondary.main'}}>
                { formatStateHeader(state)} 
              </Typography>
              <TextField
                type="search"
                variant="outlined"
                onChange={handleFilter}
                placeholder="Search/ Loan Id/ Customer/ Mobile/ Loan Type/ OEM..."
                sx={{ width: 500, backgroundColor: 'white', borderRadius: 1 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton>
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

        <Scrollbar>
          <TableContainer sx={{ width: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center">Loan ID</TableCell>
                  <TableCell align="center">Customer Name</TableCell>
                  <TableCell align="center">Mobile No</TableCell>
                  <TableCell align="center">Loan Type</TableCell>
                  {showDealer && <TableCell align="center">Dealer Name</TableCell>}
                  {includes(['pending_loans', 'all', 'closed'],state) && <TableCell align="center">Loan State</TableCell>}
                  {/* {includes(['pending_loans', 'all'],state) && <>
                    <TableCell align="center">KYC Status</TableCell>
                    <TableCell align="center">CIBIL</TableCell>
                  </>} */}
                  {state === 'loan_disbursed_with_pdd' && <>
                    <TableCell align="center">PDD Count</TableCell>
                    <TableCell align="center">PDD</TableCell>
                  </>}
                  <TableCell align="center">Created At</TableCell>
                  {includes(['pending_loans'],state) && <TableCell align="center">Download DO</TableCell>}

                  {state === 'loan_disbursed' && <>
                    <TableCell align="center">Payment Status</TableCell>
                    <TableCell align="center">Payment Ref No</TableCell>
                    <TableCell align="center">Payment Date</TableCell>
                    <TableCell align="center">No. of Emi Due</TableCell>
                    <TableCell align="center">Net Emi Due (INR)</TableCell>
                    <TableCell align="center">Net Due (INR)</TableCell>
                  </>}
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredValue.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((v, index) => (
                  <TableRow hover key={index}>
                    <TableCell
                      align="center"
                      style={{ cursor: 'pointer', color: 'blue' }}
                      onClick={() => redirect(v)}
                    >
                      {v?.loan_id}
                    </TableCell>
                    <TableCell align="center">{v?.customer_name}</TableCell>
                    <TableCell align="center">{v?.mobile || 'N/A'}</TableCell>
                    <TableCell align="center">{v?.type}</TableCell>
                    {showDealer && <TableCell align="center">{v?.firm_name}</TableCell>}
                    {includes(['pending_loans', 'all', 'closed'],state) && <TableCell align="center">{v?.state  === 'loan_disbursed_with_pdd' ? 'pdd' : v?.state}</TableCell>}
                    {/* {includes(['pending_loans', 'all'],state) && <>
                      <TableCell align="center" style={{color: v?.kyc_verified ? 'green' : 'orange'}}>{v?.kyc_verified ? 'verified' :'pending'}</TableCell>
                      <TableCell align="center" style={{color: v?.cibil_status ? 'green' : 'orange'}}>{v?.cibil_status ? 'done' :'pending'}</TableCell>
                  </>} */}
                  {state === 'loan_disbursed_with_pdd' && <>
                    <TableCell align="center">{v?.deviation_count}</TableCell>
                    <TableCell align="center" sx={{ maxWidth: 150, wordWrap: 'break-word', whiteSpace: 'normal', }}>{v?.deviation_codes}</TableCell>
                  </>}
                    <TableCell align="center">{moment(v?.created_at).format('DD-MM-YYYY')}</TableCell>
                  {includes(['pending_loans'],state) && (
                    <TableCell align="center">
                      {v?.move_to_evcred ? (
                        <Button
                          size="small"
                          startIcon={<FileDownloadIcon />}
                          onClick={() => handleDownloadDo(v)}
                          disabled={downloadingLoanId === v?.loan_id}
                        >
                          {downloadingLoanId === v?.loan_id ? 'Preparing...' : 'Download'}
                        </Button>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                  )}

                  {state === 'loan_disbursed' && <>
                    <TableCell align="center" style={{color: v?.is_dealer_payment_done ? 'green' : 'orange'}}>{v?.is_dealer_payment_done ? 'done' :'pending'}</TableCell>
                    <TableCell align="center">{v?.payment_ref_no}</TableCell>
                    <TableCell align="center">{moment(v?.ledger_date).format('DD-MM-YYYY')}</TableCell>
                    <TableCell align="center">{v?.no_of_emi_due}</TableCell>
                    <TableCell align="center"> {new Intl.NumberFormat('en-IN').format(v?.net_emi_due)}</TableCell>
                    <TableCell align="center"> {new Intl.NumberFormat('en-IN').format(v?.net_due)}</TableCell>

                  </>}
                  </TableRow>
                ))}

                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}

                {isUserNotFound && (
                  <TableRow>
                    <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                      <SearchNotFound searchQuery={''} />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>

        <TablePagination
          rowsPerPageOptions={[25, 50, 100, 500]}
          component="div"
          count={value.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>}
    </Page>
  );
}
